import { useCallback } from 'react';

import { useAnalytics } from '@bonusly/who-am-i';

type AmplitudeRecognitionType = 'peer_to_peer_recognition' | 'manual_award' | 'automated_award' | 'claimable_award';

interface AmplitudeEventProperties {
	amount?: number;
	celebrationType?: CelebrationType;
	display?: string;
	gifIncluded?: boolean;
	hashtagsCount?: number;
	hasLink?: boolean;
	imageIncluded?: boolean;
	isThankYou?: boolean;
	reason?: string;
	recipientsCount?: number;
	result?: 'success' | 'failure';
	url?: string;
	userTagsCount?: number;
	wordCount?: number;
	recognitionType?: AmplitudeRecognitionType;
}

type CelebrationType = 'birthday' | 'welcome' | 'work_anniversary' | '';

export type ViewChangeEventProps = Required<Pick<AmplitudeEventProperties, 'display'>>;

export type CommentStartEventProps = Pick<AmplitudeEventProperties, 'celebrationType' | 'recognitionType' | 'isThankYou'>;

export type CommentSubmitFailureEventProps = Pick<AmplitudeEventProperties, 'reason'>;

export type CommentSubmitSuccessEventProps = Required<
	Pick<
		AmplitudeEventProperties,
		'amount' | 'gifIncluded' | 'hashtagsCount' | 'imageIncluded' | 'recipientsCount' | 'userTagsCount' | 'wordCount' | 'recognitionType' | 'isThankYou'
	>
>;

export enum EventName {
	CommentStart = 'Monarch Comment Box Start',
	CommentSubmit = 'Monarch Comment Box Submit',
	ViewChange = 'Monarch Bonus Feed View Change',
	SuggestedThankYouClicked = 'Suggested Thank You Clicked',
	SuggestedThankYouToggled = 'Toggle Suggested Thank You',
	SuggestedThankYouGenerated = 'Generate New Thank You',
}

enum Events {
	CommentAdded = 'Comment Added',
}

export const useBonusAnalytics = () => {
	const { track, trackEvent } = useAnalytics();

	const trackCommentStart = useCallback(
		(eventProps: CommentStartEventProps) => {
			track(EventName.CommentStart, {
				...eventProps,
			});
		},
		[track]
	);

	const trackCommentSubmitFailure = useCallback(
		(eventProps?: CommentSubmitFailureEventProps) => {
			track(EventName.CommentSubmit, {
				...eventProps,
				result: 'failure',
			});
		},
		[track]
	);
	const trackCommentSubmitSuccess = useCallback(
		(eventProps: CommentSubmitSuccessEventProps) => {
			track(EventName.CommentSubmit, {
				...eventProps,
				result: 'success',
			});
			trackEvent(Events.CommentAdded);
		},
		[track, trackEvent]
	);

	const trackViewChange = useCallback(
		(eventProps: ViewChangeEventProps) => {
			track(EventName.ViewChange, {
				...eventProps,
			});
		},
		[track]
	);

	const trackClickSuggestedThankYou = useCallback(() => {
		track(EventName.SuggestedThankYouClicked);
	}, [track]);

	const trackToggleSuggestedThankYous = useCallback(
		(eventProps: ViewChangeEventProps) => {
			track(EventName.SuggestedThankYouToggled, { ...eventProps });
		},
		[track]
	);

	return {
		trackCommentStart,
		trackCommentSubmitFailure,
		trackCommentSubmitSuccess,
		trackViewChange,
		trackClickSuggestedThankYou,
		trackToggleSuggestedThankYous,
	};
};
